import Mousetrap, { ExtendedKeyboardEvent } from 'mousetrap';
import { DependencyList, useCallback, useEffect, useRef } from 'react';

export default function useMousetrap(
  keys: string | string[],
  callback: (e: ExtendedKeyboardEvent, combo: string) => any,
  options?: {
    action?: string;
    global?: boolean;
    deps?: DependencyList;
  },
) {
  const { action, global, deps } = {
    deps: [],
    ...options,
  };

  // See https://github.com/ccampbell/mousetrap/issues/288#issuecomment-185843934
  const mousetrapRef = useRef(new Mousetrap());
  useEffect(() => {
    if (global) {
      mousetrapRef.current.stopCallback = () => false;
    } else {
      mousetrapRef.current.stopCallback = defaultStopCallback;
    }
  }, [global]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedCallback = useCallback(callback, deps);

  useEffect(() => {
    const mousetrap = mousetrapRef.current;
    mousetrap.bind(keys, memoizedCallback, action);
    return () => {
      mousetrap.unbind(keys, action);
    };
  }, [action, memoizedCallback, keys, global]);
}

function defaultStopCallback(_e: ExtendedKeyboardEvent, element: Element, _combo: string) {
  // Adapted from https://craig.is/killing/mice#api.stopCallback)
  // We allow keyboard commands while inputs that won't accept text are focused.
  return (
    element.tagName === 'SELECT' ||
    element.tagName === 'TEXTAREA' ||
    (element instanceof HTMLInputElement &&
      element.type !== 'checkbox' &&
      element.type !== 'radio') ||
    (element instanceof HTMLElement && element?.contentEditable === 'true')
  );
}
